import type { AuthMethod } from "@defuse-protocol/internal-utils"
import { authIdentity } from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import { settings } from "@src/components/DefuseSDK/constants/settings"
import {
  getPriceForToken,
  type TokenPrices,
} from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import {
  type BalanceMapping,
  balancesSelector,
  type Events as DepositedBalanceEvents,
  depositedBalanceMachine,
} from "@src/components/DefuseSDK/features/machines/depositedBalanceMachine"
import { emitEvent } from "@src/components/DefuseSDK/services/emitter"
import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import { assert } from "@src/components/DefuseSDK/utils/assert"
import { isAuroraVirtualChain } from "@src/components/DefuseSDK/utils/blockchain"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import {
  isSamePaymentFamily,
  pickDefaultPaymentToken,
} from "@src/components/DefuseSDK/utils/paymentTokenSelection"
import { getDefuseAssetId } from "@src/components/DefuseSDK/utils/token"
import {
  compareAmounts,
  computeTotalBalanceDifferentDecimals,
  netDownAmount,
} from "@src/components/DefuseSDK/utils/tokenUtils"
import {
  type JourneyProvenance,
  journeyAnalytics,
} from "@src/features/analytics/journeyAnalytics"
import { isNearIntentsNetwork } from "@src/shared/utils/networks"
import { logger } from "@src/utils/logger"
import type { providers } from "near-api-js"
import { formatUnits } from "viem"
import {
  type ActorRefFrom,
  and,
  assign,
  emit,
  type InputFrom,
  raise,
  sendTo,
  setup,
} from "xstate"
import { trackUniversalSendRejected } from "../analytics/withdrawRequestEvents"
import {
  type AmountModeFallbackNotice,
  exceedsBalance,
} from "../utils/exactOutBudget"
import {
  freshDestinationInput,
  freshDestinationKey,
  freshDestinationTelemetryPayload,
} from "../utils/freshDestinationGate"
import {
  type ExactInputAmount,
  matchesWithdrawQuoteInput,
} from "../utils/withdrawQuoteUtils"
import {
  getParsedWithdrawRecipientAddress,
  parsedWithdrawRecipientMatchesNetwork,
} from "../utils/withdrawRecipient"
import type {
  ParentEvents as BackgroundWithdraw1csQuoterParentEvents,
  WithdrawQuote1csInput,
} from "./backgroundWithdraw1csQuoterMachine"
import { backgroundWithdraw1csQuoterMachine } from "./backgroundWithdraw1csQuoterMachine"
import {
  type Output as Withdraw1csMachineOutput,
  withdraw1csMachine,
} from "./withdraw1csMachine"
import {
  type Events as WithdrawFormEvents,
  type ParentEvents as WithdrawFormParentEvents,
  type State as WithdrawFormState,
  withdrawFormReducer,
} from "./withdrawFormReducer"
import { requestWithdrawQuote } from "./withdrawQuoteActions"

function safeParseBigInt(value: string): bigint | null {
  try {
    return BigInt(value)
  } catch {
    return null
  }
}

type QuoteResult =
  | {
      tag: "ok"
      value: {
        quote: {
          amountIn: string
          amountOut: string
          deadline?: string
          timeEstimate?: number
        }
        appFee: [string, bigint][]
      }
    }
  | { tag: "err"; value: { reason: string } }

export type AmountMode =
  | QuoteRequest.swapType.EXACT_INPUT
  | QuoteRequest.swapType.EXACT_OUTPUT

export type QuoteRejectionReason = "EXCEEDS_BALANCE"

type SerializableExactInputAmount = { amount: string; decimals: number }

export type WithdrawAnalyticsFlow = "withdraw" | "perps_fund"

export type WithdrawAnalyticsFacts = Readonly<{
  asset_id: string
  asset_symbol: string
  amount: string
  source_chain: string
  destination_chain: string
}>

type WithdrawAnalyticsFor<F extends WithdrawAnalyticsFlow> = Readonly<{
  journey: JourneyProvenance<F>
  facts: WithdrawAnalyticsFacts
}>

export type WithdrawAnalytics =
  | WithdrawAnalyticsFor<"withdraw">
  | WithdrawAnalyticsFor<"perps_fund">

type WithdrawAnalyticsSurface = {
  withdraw: "withdraw"
  perps_fund: "perps"
}

export type WithdrawAnalyticsEvent = {
  [F in WithdrawAnalyticsFlow]: WithdrawAnalyticsFor<F>["facts"] & {
    journey: WithdrawAnalyticsFor<F>["journey"]
    surface: WithdrawAnalyticsSurface[F]
  }
}[WithdrawAnalyticsFlow]

export function toWithdrawAnalyticsEvent(
  analytics: WithdrawAnalytics
): WithdrawAnalyticsEvent {
  if (analytics.journey.flow === "perps_fund") {
    return {
      ...analytics.facts,
      journey: analytics.journey,
      surface: "perps",
    }
  }

  return {
    ...analytics.facts,
    journey: analytics.journey,
    surface: "withdraw",
  }
}

type QuoteEvent = Extract<
  BackgroundWithdraw1csQuoterParentEvents,
  { type: "NEW_WITHDRAW_1CS_QUOTE" }
>
type QuoteApiResult = QuoteEvent["params"]["result"]

export type Context = {
  intentCreationResult: Withdraw1csMachineOutput | null
  tokenList: TokenInfo[]
  tokenPrices: TokenPrices
  depositedBalanceRef: ActorRefFrom<typeof depositedBalanceMachine>
  withdrawFormRef: ActorRefFrom<typeof withdrawFormReducer>
  backgroundQuoterRef: ActorRefFrom<
    typeof backgroundWithdraw1csQuoterMachine
  > | null
  submitDeps: {
    userAddress: string
    userChainType: AuthMethod
    nearClient: providers.Provider
  } | null
  quoteResult: QuoteResult | null
  quoteInput: WithdrawQuote1csInput | null
  quoteRejectionReason: QuoteRejectionReason | null
  referral?: string
  slippageBasisPoints: number
  slippageExplicitlySet: boolean
  userAddress: string | null
  userChainType: AuthMethod | null
  appFeeRecipient: string
  amountMode: AmountMode
  exactInputAmount: ExactInputAmount | null
  syncExactInputQuoteToAmount: boolean
  priceChangeDialog: null | {
    pendingNewOppositeAmount: { amount: bigint; decimals: number }
    previousOppositeAmount: { amount: bigint; decimals: number }
  }
  requiredFreshnessKey: string | null
  amountModeFallbackNotice: AmountModeFallbackNotice | null
  isConfidential: boolean
  variant: "default" | "fund"
  analytics: WithdrawAnalytics | null
}

function createWithdrawAnalytics(context: Context): WithdrawAnalytics | null {
  const quoteInput = context.quoteInput
  const quoteResult = context.quoteResult
  if (quoteInput == null || quoteResult?.tag !== "ok") return null

  const sourceAmount =
    context.amountMode === QuoteRequest.swapType.EXACT_OUTPUT
      ? (() => {
          const amount = safeParseBigInt(quoteResult.value.quote.amountIn)
          return amount == null
            ? null
            : { amount, decimals: quoteInput.tokenIn.decimals }
        })()
      : quoteInput.amount
  if (sourceAmount == null || sourceAmount.amount <= 0n) return null

  const amount = formatTokenValue(sourceAmount.amount, sourceAmount.decimals)
  const sourcePrice = getPriceForToken(quoteInput.tokenIn, context.tokenPrices)
  const numericValue =
    sourcePrice != null && Number.isFinite(sourcePrice) && sourcePrice > 0
      ? Number(amount) * sourcePrice
      : null
  const valuation =
    numericValue != null && Number.isFinite(numericValue) && numericValue >= 0
      ? { valueUsd: numericValue, valueQuality: "estimated" as const }
      : { valueUsd: null, valueQuality: "unavailable" as const }
  const formContext = context.withdrawFormRef.getSnapshot().context
  const journeyBase = {
    journeyId: journeyAnalytics.newId(),
    privacy: context.isConfidential
      ? ("confidential" as const)
      : ("public" as const),
    valuation,
  }
  const facts = {
    asset_id: quoteInput.tokenIn.defuseAssetId,
    asset_symbol: quoteInput.tokenIn.symbol,
    amount,
    source_chain: "near_intents",
    destination_chain: formContext.blockchain,
  }

  if (context.variant === "fund") {
    return {
      journey: { ...journeyBase, flow: "perps_fund" as const },
      facts,
    }
  }

  return {
    journey: { ...journeyBase, flow: "withdraw" as const },
    facts,
  }
}

function toQuoteResult(result: QuoteApiResult): QuoteResult {
  if ("ok" in result) {
    return {
      tag: "ok",
      value: result.ok,
    }
  }

  return {
    tag: "err",
    value: { reason: result.err },
  }
}

function matchesWithdrawQuoteContext(
  context: Context,
  event: QuoteEvent
): boolean {
  return matchesWithdrawQuoteInput({
    formContext: context.withdrawFormRef.getSnapshot().context,
    quoteInput: event.params.quoteInput,
    userAddress: context.userAddress,
    userChainType: context.userChainType,
    tokenList: context.tokenList,
    balances: balancesSelector(context.depositedBalanceRef?.getSnapshot()),
    exactInputAmount: context.exactInputAmount,
    isConfidential: context.isConfidential,
  })
}

function hasValidRecipientPair(formContext: WithdrawFormState): boolean {
  if (formContext.parsedRecipient == null) return false
  return parsedWithdrawRecipientMatchesNetwork(
    formContext.parsedRecipient,
    isNearIntentsNetwork(formContext.blockchain)
  )
}

// Shared derivation for the exact-out over-balance guards: confirms the quote
// is a fresh, parseable exact-out quote and returns its amountIn alongside the
// spendable balance. Null when any precondition fails.
function resolveFreshExactOutBalance(
  context: Context,
  event: QuoteEvent
): { parsedAmountIn: bigint; availableBalance: TokenValue } | null {
  if (context.amountMode !== QuoteRequest.swapType.EXACT_OUTPUT) return null
  if (!("ok" in event.params.result)) return null
  if (!matchesWithdrawQuoteContext(context, event)) return null

  const parsedAmountIn = safeParseBigInt(event.params.result.ok.quote.amountIn)
  if (parsedAmountIn == null) return null

  const availableBalance = computeTotalBalanceDifferentDecimals(
    event.params.quoteInput.tokenIn,
    context.depositedBalanceRef.getSnapshot().context.balances,
    { strict: false }
  )
  if (availableBalance == null) return null

  return { parsedAmountIn, availableBalance }
}

type PassthroughEvent = {
  type: "ONE_CLICK_SETTLED"
  data: {
    depositAddress: string
    status: string
    tokenIn: TokenInfo
    tokenOut: TokenInfo
  }
}

type EmittedEvents = PassthroughEvent | { type: "INTENT_PUBLISHED" }

const fallbackFreshExactOutOverBalanceTransition = {
  reenter: true,
  guard: "shouldFallbackFreshExactOutOverBalance",
  actions: {
    type: "applyExactInFallback",
    params: { notice: null },
  },
} as const

export const withdrawUIMachine = setup({
  types: {
    input: {} as {
      tokenIn: TokenInfo
      tokenOut: BaseTokenInfo
      tokenList: TokenInfo[]
      tokenPrices?: TokenPrices
      referral?: string
      slippageBasisPoints: number
      appFeeRecipient: string
      isConfidential?: boolean
      paymentToken?: TokenInfo
      initialAmount?: string
      initialParsedAmount?: SerializableExactInputAmount | null
      initialBlockchain?: SupportedChainName | "near_intents"
      initialRecipient?: string
      variant?: "default" | "fund"
    },
    context: {} as Context,
    events: {} as
      | {
          type: "BALANCE_CHANGED"
          params: {
            changedBalanceMapping: BalanceMapping
          }
        }
      | BackgroundWithdraw1csQuoterParentEvents
      | DepositedBalanceEvents
      | WithdrawFormEvents
      | WithdrawFormParentEvents
      | PassthroughEvent
      | {
          type: "WITHDRAW_1CS_QUOTE_ERROR"
          params: {
            reason: string
          }
        }
      | {
          type: "REQUEST_REVIEW"
          params: NonNullable<Context["submitDeps"]>
        }
      | { type: "CANCEL_REVIEW" }
      | {
          type: "FRESH_DESTINATION_GATE_CHANGED"
          params: { key: string; required: boolean }
        }
      | {
          type: "SET_SLIPPAGE"
          params: { slippageBasisPoints: number }
        }
      | {
          type: "SET_TOKEN_PRICES"
          params: { tokenPrices: TokenPrices }
        }
      | {
          type: "SET_AMOUNT_MODE"
          params: {
            amountMode: AmountMode
            exactInputAmount?: SerializableExactInputAmount | null
            syncQuoteToAmount?: boolean
          }
        }
      | {
          type: "PICK_PAYMENT_TOKEN"
          params: {
            paymentToken: TokenInfo
          }
        }
      | {
          type: "FALLBACK_TO_EXACT_IN"
          params: { notice: AmountModeFallbackNotice | null }
        }
      | {
          type: "EXECUTION_QUOTE_READY"
          params: {
            newAmountIn: { amount: bigint; decimals: number }
            newOppositeAmount: { amount: bigint; decimals: number }
            previousOppositeAmount: { amount: bigint; decimals: number }
          }
        }
      | {
          type: "CONFIRM_WITHDRAWAL"
          params?: { freshnessConfirmationKey?: string | null }
        },

    emitted: {} as EmittedEvents,

    children: {} as {
      withdrawRef: "withdraw1csActor"
    },
  },
  actors: {
    // biome-ignore lint/suspicious/noExplicitAny: bypass xstate+ts bloating; be careful when interacting with `depositedBalanceActor` string
    depositedBalanceActor: depositedBalanceMachine as any,
    withdraw1csActor: withdraw1csMachine,
    withdrawFormActor: withdrawFormReducer,
    backgroundWithdraw1csQuoterActor: backgroundWithdraw1csQuoterMachine,
  },
  actions: {
    logError: (_, event: { error: unknown }) => {
      logger.error(event.error)
    },

    setAuthenticatedUserIdentity: assign({
      userAddress: (
        _,
        value: { userAddress: string; userChainType: AuthMethod }
      ) => value.userAddress,
      userChainType: (
        _,
        value: { userAddress: string; userChainType: AuthMethod }
      ) => value.userChainType,
    }),
    clearAuthenticatedUserIdentity: assign({
      userAddress: null,
      userChainType: null,
    }),
    setIntentCreationResult: assign({
      intentCreationResult: (_, value: Withdraw1csMachineOutput) => value,
    }),
    clearIntentCreationResult: assign({ intentCreationResult: null }),

    setAnalyticsJourney: assign({
      analytics: ({ context }) => createWithdrawAnalytics(context),
    }),
    emitAnalyticsJourneyStarted: ({ context }) => {
      if (context.analytics == null) return
      journeyAnalytics.started(toWithdrawAnalyticsEvent(context.analytics))
    },
    emitWithdrawalFailed: ({ context }, output: Withdraw1csMachineOutput) => {
      if (output.tag !== "err") return

      trackUniversalSendRejected(
        context.variant,
        "reason" in output.value ? output.value.reason : undefined
      )

      const withdrawContext = context.withdrawFormRef.getSnapshot().context
      assert(withdrawContext.parsedAmount != null, "parsedAmount is null")
      emitEvent("withdrawal_failed", {
        token: withdrawContext.tokenIn.symbol,
        amount: withdrawContext.parsedAmount.toString(),
        to_chain: withdrawContext.tokenOut.defuseAssetId,
        error_reason:
          "reason" in output.value ? output.value.reason : "unknown",
      })
    },

    passthroughEvent: emit((_, event: PassthroughEvent) => event),

    setSubmitDeps: assign({
      submitDeps: (_, value: Context["submitDeps"]) => value,
    }),
    setQuoteResult: assign({
      quoteResult: (_, val: Context["quoteResult"]) => val,
    }),
    clearQuoteResult: assign({
      quoteResult: null,
    }),
    setQuoteInput: assign({
      quoteInput: (_, val: Context["quoteInput"]) => val,
    }),
    clearQuoteInput: assign({
      quoteInput: null,
    }),
    clearQuoteRejectionReason: assign({
      quoteRejectionReason: null,
    }),
    setQuoteRejectionReason: assign({
      quoteRejectionReason: (
        _,
        params: { reason: QuoteRejectionReason | null }
      ) => params.reason,
    }),
    setSlippage: assign({
      slippageBasisPoints: (_, value: number) => value,
      slippageExplicitlySet: true,
    }),
    setTokenPrices: assign({
      tokenPrices: (_, value: { tokenPrices: TokenPrices }) =>
        value.tokenPrices,
    }),
    setAmountMode: assign({
      amountMode: (
        _,
        value: {
          amountMode: AmountMode
          exactInputAmount?: SerializableExactInputAmount | null
          syncQuoteToAmount?: boolean
        }
      ) => value.amountMode,
      exactInputAmount: (
        _,
        value: {
          amountMode: AmountMode
          exactInputAmount?: SerializableExactInputAmount | null
          syncQuoteToAmount?: boolean
        }
      ) =>
        value.exactInputAmount != null
          ? {
              amount: BigInt(value.exactInputAmount.amount),
              decimals: value.exactInputAmount.decimals,
            }
          : null,
      syncExactInputQuoteToAmount: (
        _,
        value: {
          amountMode: AmountMode
          exactInputAmount?: SerializableExactInputAmount | null
          syncQuoteToAmount?: boolean
        }
      ) =>
        value.amountMode === QuoteRequest.swapType.EXACT_INPUT &&
        value.syncQuoteToAmount === true,
    }),
    setAmountModeExactOutput: assign({
      amountMode: (_) => QuoteRequest.swapType.EXACT_OUTPUT as AmountMode,
      exactInputAmount: null,
      syncExactInputQuoteToAmount: false,
    }),
    applyExactInFallback: assign({
      amountMode: (_) => QuoteRequest.swapType.EXACT_INPUT as AmountMode,
      exactInputAmount: null,
      syncExactInputQuoteToAmount: false,
      amountModeFallbackNotice: (
        _,
        params: { notice: AmountModeFallbackNotice | null }
      ) => params.notice,
      quoteRejectionReason: (
        _,
        params: { notice: AmountModeFallbackNotice | null }
      ) => (params.notice != null ? "EXCEEDS_BALANCE" : null),
    }),
    clearAmountModeFallbackNotice: assign({
      amountModeFallbackNotice: null,
    }),
    clearExactInputQuoteToAmountSync: assign({
      syncExactInputQuoteToAmount: false,
    }),
    syncExactInputQuoteAmountToForm: ({ context, event }) => {
      if (!context.syncExactInputQuoteToAmount) return
      if (context.amountMode !== QuoteRequest.swapType.EXACT_INPUT) return
      if (event.type !== "NEW_WITHDRAW_1CS_QUOTE") return
      if (!("ok" in event.params.result)) return

      const formContext = context.withdrawFormRef.getSnapshot().context
      const amountOut = event.params.result.ok.quote.amountOut
      const parsedAmountOut = safeParseBigInt(amountOut)
      if (parsedAmountOut == null) return

      context.withdrawFormRef.send({
        type: "WITHDRAW_FORM.UPDATE_AMOUNT",
        params: {
          amount: formatUnits(parsedAmountOut, formContext.tokenOut.decimals),
          parsedAmount: {
            amount: amountOut,
            decimals: formContext.tokenOut.decimals,
          },
          notifyParent: false,
        },
      })
    },
    relayToDepositedBalanceRef: sendTo(
      "depositedBalanceRef",
      (_, event: DepositedBalanceEvents) => event
    ),
    sendToDepositedBalanceRefRefresh: sendTo("depositedBalanceRef", (_) => ({
      type: "REQUEST_BALANCE_REFRESH",
    })),
    sendToDepositedBalanceRefRemoveAccount: sendTo(
      "depositedBalanceRef",
      (_, params: { depositAddress: string }) => ({
        type: "REMOVE_ACCOUNT",
        params: {
          accountId: authIdentity.authHandleToIntentsUserId(
            params.depositAddress,
            "near"
          ),
        },
      })
    ),

    relayToWithdrawFormRef: sendTo(
      "withdrawFormRef",
      (_, event: WithdrawFormEvents) => event
    ),
    relayPickPaymentTokenToWithdrawFormRef: ({ context, event }) => {
      if (event.type !== "PICK_PAYMENT_TOKEN") return
      context.withdrawFormRef.send({
        type: "WITHDRAW_FORM.PICK_PAYMENT_TOKEN",
        params: event.params,
      })
    },

    applyDefaultPaymentToken: ({ context, event }) => {
      if (event.type !== "WITHDRAW_FORM_FIELDS_CHANGED") return
      if (!event.fields.includes("tokenIn")) return
      const balances =
        context.depositedBalanceRef?.getSnapshot()?.context.balances
      if (!balances) return

      const defaultToken = pickDefaultPaymentToken({
        destinationToken: context.withdrawFormRef.getSnapshot().context.tokenIn,
        heldTokens: context.tokenList,
        balances,
        tokenPrices: context.tokenPrices,
      })
      if (defaultToken == null) return

      const currentPaymentToken =
        context.withdrawFormRef?.getSnapshot()?.context.paymentToken
      if (
        currentPaymentToken != null &&
        getDefuseAssetId(currentPaymentToken) === getDefuseAssetId(defaultToken)
      ) {
        return
      }

      context.withdrawFormRef?.send({
        type: "WITHDRAW_FORM.UPDATE_PAYMENT_TOKEN",
        params: { paymentToken: defaultToken },
      })
    },

    applyDefaultPaymentTokenIfNotCustomized: ({ context }) => {
      const formContext = context.withdrawFormRef?.getSnapshot()?.context
      if (!formContext) return
      if (formContext.paymentTokenCustomized) return
      if (
        formContext.paymentToken != null &&
        !isSamePaymentFamily(formContext.paymentToken, formContext.tokenIn)
      ) {
        return
      }

      const balances =
        context.depositedBalanceRef?.getSnapshot()?.context.balances
      if (!balances) return

      const defaultToken = pickDefaultPaymentToken({
        destinationToken: formContext.tokenIn,
        heldTokens: context.tokenList,
        balances,
        tokenPrices: context.tokenPrices,
      })
      if (defaultToken == null) return

      if (
        formContext.paymentToken != null &&
        getDefuseAssetId(formContext.paymentToken) ===
          getDefuseAssetId(defaultToken)
      ) {
        return
      }

      context.withdrawFormRef?.send({
        type: "WITHDRAW_FORM.UPDATE_PAYMENT_TOKEN",
        params: { paymentToken: defaultToken },
      })
    },

    setExecutionQuote: assign({
      priceChangeDialog: (
        _,
        params: {
          newOppositeAmount: { amount: bigint; decimals: number }
          previousOppositeAmount: { amount: bigint; decimals: number }
        }
      ) => {
        if (
          params.newOppositeAmount.amount < params.previousOppositeAmount.amount
        ) {
          return {
            pendingNewOppositeAmount: params.newOppositeAmount,
            previousOppositeAmount: params.previousOppositeAmount,
          }
        }
        return null
      },
    }),
    updateQuoteAmounts: assign({
      quoteResult: (
        { context },
        params: { newAmountIn: bigint; newAmountOut: bigint }
      ) => {
        if (context.quoteResult?.tag !== "ok") return context.quoteResult
        return {
          ...context.quoteResult,
          value: {
            ...context.quoteResult.value,
            quote: {
              ...context.quoteResult.value.quote,
              amountIn: params.newAmountIn.toString(),
              amountOut: params.newAmountOut.toString(),
            },
          },
        }
      },
    }),
    clearExecutionQuote: assign({
      priceChangeDialog: null,
    }),
    setFreshnessGateRequirement: assign({
      requiredFreshnessKey: (
        { context },
        params: { key: string; required: boolean }
      ) => {
        const formContext = context.withdrawFormRef.getSnapshot().context
        const input = freshDestinationInput({
          network: formContext.blockchain,
          recipient:
            formContext.parsedRecipient == null
              ? null
              : getParsedWithdrawRecipientAddress(formContext.parsedRecipient),
        })
        const currentKey = input == null ? null : freshDestinationKey(input)
        if (currentKey !== params.key) return context.requiredFreshnessKey
        return params.required ? params.key : null
      },
    }),
    clearFreshnessGateRequirement: assign({
      requiredFreshnessKey: null,
    }),
    sendToWithdrawRefConfirm: sendTo("withdrawRef", () => ({
      type: "CONFIRMED",
    })),

    emitFreshnessWarningProceeded: ({ context }) => {
      if (context.requiredFreshnessKey == null) return
      const formContext = context.withdrawFormRef.getSnapshot().context
      const input = freshDestinationInput({
        network: formContext.blockchain,
        recipient:
          formContext.parsedRecipient == null
            ? null
            : getParsedWithdrawRecipientAddress(formContext.parsedRecipient),
      })
      if (
        input == null ||
        freshDestinationKey(input) !== context.requiredFreshnessKey
      ) {
        return
      }
      emitEvent(
        "send_freshness_warning_proceeded",
        freshDestinationTelemetryPayload(input, true)
      )
    },

    emitEventIntentPublished: emit(() => ({
      type: "INTENT_PUBLISHED" as const,
    })),

    requestQuote: ({ context, self }) => {
      const formContext = context.withdrawFormRef.getSnapshot().context
      const balances = balancesSelector(
        context.depositedBalanceRef.getSnapshot()
      )

      requestWithdrawQuote({
        backgroundQuoterRef: context.backgroundQuoterRef,
        formContext,
        userAddress: context.userAddress,
        userChainType: context.userChainType,
        isConfidential: context.isConfidential,
        tokenList: context.tokenList,
        balances,
        balancesLoaded:
          context.depositedBalanceRef.getSnapshot().context.balancesLoaded,
        amountMode: context.amountMode,
        exactInputAmount: context.exactInputAmount,
        slippageBasisPoints: context.slippageBasisPoints,
        slippageExplicitlySet: context.slippageExplicitlySet,
        tokenPrices: context.tokenPrices,
        onQuoteError: (reason) => {
          self.send({
            type: "WITHDRAW_1CS_QUOTE_ERROR",
            params: { reason },
          })
        },
        onFallbackExactIn: () => {
          self.send({
            type: "FALLBACK_TO_EXACT_IN",
            params: { notice: null },
          })
        },
      })
    },

    pauseQuoter: ({ context }) => {
      context.backgroundQuoterRef?.send({ type: "PAUSE" })
    },
  },
  guards: {
    // Quotes can be fetched before loose-funds acknowledgement; submission cannot.
    isQuotable: ({ context }) => {
      const formContext = context.withdrawFormRef.getSnapshot().context
      return (
        formContext.parsedAmount != null &&
        formContext.parsedAmount.amount > 0n &&
        hasValidRecipientPair(formContext) &&
        context.userAddress != null &&
        context.userChainType != null
      )
    },

    isWithdrawParamsComplete: ({ context }) => {
      const formContext = context.withdrawFormRef.getSnapshot().context
      return (
        formContext.parsedAmount != null &&
        formContext.parsedAmount.amount > 0n &&
        hasValidRecipientPair(formContext) &&
        formContext.cexFundsLooseConfirmation !== "not_confirmed" &&
        context.userAddress != null &&
        context.userChainType != null
      )
    },

    isQuoteOk: ({ context }) => {
      if (context.quoteResult?.tag !== "ok") return false
      const { amountIn, amountOut } = context.quoteResult.value.quote
      const parsedAmountIn = safeParseBigInt(amountIn)
      if (parsedAmountIn == null || safeParseBigInt(amountOut) == null) {
        return false
      }

      if (
        context.amountMode === QuoteRequest.swapType.EXACT_OUTPUT &&
        context.quoteInput != null &&
        exceedsBalance(
          parsedAmountIn,
          computeTotalBalanceDifferentDecimals(
            context.quoteInput.tokenIn,
            context.depositedBalanceRef.getSnapshot().context.balances
          )
        )
      ) {
        return false
      }

      return true
    },

    isExecutionQuoteAffordable: (
      { context },
      params: { newAmountIn: { amount: bigint; decimals: number } }
    ) => {
      if (
        context.amountMode !== QuoteRequest.swapType.EXACT_OUTPUT ||
        context.quoteInput == null
      ) {
        return true
      }
      return !exceedsBalance(
        params.newAmountIn.amount,
        computeTotalBalanceDifferentDecimals(
          context.quoteInput.tokenIn,
          context.depositedBalanceRef.getSnapshot().context.balances
        )
      )
    },

    isFreshExactOutOverBalance: ({ context, event }) => {
      if (event.type !== "NEW_WITHDRAW_1CS_QUOTE") return false
      const resolved = resolveFreshExactOutBalance(context, event)
      if (resolved == null) return false

      return exceedsBalance(resolved.parsedAmountIn, resolved.availableBalance)
    },

    // Same-family exact-out quote whose amountIn (typed amount + fees) overflows
    // balance, while the typed amount alone is still spendable — retry as exact-in.
    shouldFallbackFreshExactOutOverBalance: ({ context, event }) => {
      if (event.type !== "NEW_WITHDRAW_1CS_QUOTE") return false
      const resolved = resolveFreshExactOutBalance(context, event)
      if (resolved == null) return false

      const formContext = context.withdrawFormRef.getSnapshot().context
      if (!isSamePaymentFamily(formContext.paymentToken, formContext.tokenIn)) {
        return false
      }

      return (
        exceedsBalance(resolved.parsedAmountIn, resolved.availableBalance) &&
        compareAmounts(
          event.params.quoteInput.amount,
          resolved.availableBalance
        ) <= 0
      )
    },

    isFreshWithdrawQuote: ({ context, event }) => {
      if (event.type !== "NEW_WITHDRAW_1CS_QUOTE") return false
      return matchesWithdrawQuoteContext(context, event)
    },

    freshnessGateSatisfied: ({ context, event }) => {
      if (context.requiredFreshnessKey == null) return true
      if (event.type !== "CONFIRM_WITHDRAWAL") return false
      return (
        event.params?.freshnessConfirmationKey === context.requiredFreshnessKey
      )
    },

    isIntentCreationResultError: ({ context }) =>
      context.intentCreationResult?.tag === "err",
    isOk: (_, a: { tag: "err" | "ok" }) => a.tag === "ok",
  },
}).createMachine({
  id: "withdraw-ui",

  context: ({ input, spawn, self }) => ({
    intentCreationResult: null,
    tokenList: input.tokenList,
    tokenPrices: input.tokenPrices ?? {},
    userAddress: null,
    userChainType: null,
    depositedBalanceRef: spawn("depositedBalanceActor", {
      id: "depositedBalanceRef",
      input: {
        parentRef: self,
        tokenList: input.tokenList,
        isConfidential: input.isConfidential ?? false,
      } satisfies InputFrom<typeof depositedBalanceMachine>,
    }),
    withdrawFormRef: spawn("withdrawFormActor", {
      id: "withdrawFormRef",
      input: {
        parentRef: self,
        tokenIn: input.tokenIn,
        paymentToken: input.paymentToken,
        amount: input.initialAmount,
        parsedAmount: input.initialParsedAmount,
        blockchain: input.initialBlockchain,
        recipient: input.initialRecipient,
      },
    }),
    backgroundQuoterRef: spawn("backgroundWithdraw1csQuoterActor", {
      id: "backgroundQuoterRef",
      input: { parentRef: self },
    }),
    submitDeps: null,
    quoteResult: null,
    quoteInput: null,
    quoteRejectionReason: null,
    slippageBasisPoints: input.slippageBasisPoints,
    slippageExplicitlySet: false,
    amountMode: QuoteRequest.swapType.EXACT_OUTPUT as AmountMode,
    exactInputAmount: null,
    syncExactInputQuoteToAmount: false,
    priceChangeDialog: null,
    requiredFreshnessKey: null,
    amountModeFallbackNotice: null,
    referral: input.referral,
    appFeeRecipient: input.appFeeRecipient,
    isConfidential: input.isConfidential ?? false,
    variant: input.variant ?? "default",
    analytics: null,
  }),

  on: {
    ONE_CLICK_SETTLED: {
      actions: [
        {
          type: "passthroughEvent",
          params: ({ event }) => event,
        },
        {
          type: "sendToDepositedBalanceRefRemoveAccount",
          params: ({ event }) => ({
            depositAddress: event.data.depositAddress,
          }),
        },
        "sendToDepositedBalanceRefRefresh",
      ],
    },

    LOGIN: {
      actions: [
        {
          type: "relayToDepositedBalanceRef",
          params: ({ event }) => event,
        },
        {
          type: "setAuthenticatedUserIdentity",
          params: ({ event }) => event.params,
        },
      ],
    },

    LOGOUT: {
      actions: [
        {
          type: "relayToDepositedBalanceRef",
          params: ({ event }) => event,
        },
        {
          type: "clearAuthenticatedUserIdentity",
        },
      ],
    },
  },

  states: {
    editing: {
      initial: "idle",

      on: {
        "WITHDRAW_FORM.*": {
          actions: [
            {
              type: "relayToWithdrawFormRef",
              params: ({ event }) => event,
            },
          ],
        },

        BALANCE_CHANGED: [
          {
            target: ".quoting",
            guard: "isQuotable",
            actions: [
              "applyDefaultPaymentTokenIfNotCustomized",
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
          {
            target: ".idle",
            actions: [
              "applyDefaultPaymentTokenIfNotCustomized",
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
        ],

        LOGIN: {
          actions: [
            {
              type: "relayToDepositedBalanceRef",
              params: ({ event }) => event,
            },
            {
              type: "setAuthenticatedUserIdentity",
              params: ({ event }) => event.params,
            },
            raise({ type: "WITHDRAW_FORM_FIELDS_CHANGED", fields: [] }),
          ],
        },

        WITHDRAW_FORM_FIELDS_CHANGED: [
          {
            target: ".quoting",
            guard: "isQuotable",
            actions: [
              "applyDefaultPaymentToken",
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
          {
            target: ".idle",
            actions: [
              "applyDefaultPaymentToken",
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
        ],

        SET_SLIPPAGE: [
          {
            target: ".quoting",
            guard: "isQuotable",
            actions: [
              {
                type: "setSlippage",
                params: ({ event }) => event.params.slippageBasisPoints,
              },
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
          {
            target: ".idle",
            actions: [
              {
                type: "setSlippage",
                params: ({ event }) => event.params.slippageBasisPoints,
              },
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
        ],

        SET_TOKEN_PRICES: {
          actions: [
            {
              type: "setTokenPrices",
              params: ({ event }) => event.params,
            },
            "applyDefaultPaymentTokenIfNotCustomized",
          ],
        },

        // Update the amount mode only — do NOT request a quote here. Every
        // SET_AMOUNT_MODE is paired with an amount change (manual typing resets
        // to EXACT_OUTPUT; use-max sets EXACT_INPUT), and that change drives the
        // quote with the up-to-date amount. Quoting here would run against the
        // *previous* amount (the form hasn't updated yet); if that stale amount
        // is near/over balance it triggers FALLBACK_TO_EXACT_IN, which lands
        // after the new amount and pins the quote to max — and EXACT_INPUT
        // ignores the typed amount. See the WithdrawForm watch handler.
        SET_AMOUNT_MODE: {
          actions: [
            {
              type: "setAmountMode",
              params: ({ event }) => event.params,
            },
            "clearQuoteResult",
            "clearQuoteInput",
            "clearAmountModeFallbackNotice",
            "clearQuoteRejectionReason",
          ],
        },

        PICK_PAYMENT_TOKEN: [
          {
            target: ".quoting",
            guard: "isQuotable",
            actions: [
              "setAmountModeExactOutput",
              "relayPickPaymentTokenToWithdrawFormRef",
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
          {
            target: ".idle",
            actions: [
              "setAmountModeExactOutput",
              "relayPickPaymentTokenToWithdrawFormRef",
              "clearQuoteResult",
              "clearQuoteInput",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
            ],
          },
        ],

        FALLBACK_TO_EXACT_IN: {
          target: ".quoting",
          reenter: true,
          actions: {
            type: "applyExactInFallback",
            params: ({ event }) => event.params,
          },
        },

        NEW_WITHDRAW_1CS_QUOTE: [
          {
            target: ".quoting",
            ...fallbackFreshExactOutOverBalanceTransition,
          },
          {
            target: ".idle",
            guard: "isFreshExactOutOverBalance",
            actions: [
              "clearAmountModeFallbackNotice",
              {
                type: "setQuoteRejectionReason",
                params: { reason: "EXCEEDS_BALANCE" },
              },
              {
                type: "setQuoteResult",
                params: ({ event }) => toQuoteResult(event.params.result),
              },
              {
                type: "setQuoteInput",
                params: ({ event }) => event.params.quoteInput,
              },
              "clearExactInputQuoteToAmountSync",
            ],
          },
          {
            guard: "isFreshWithdrawQuote",
            actions: [
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
              {
                type: "setQuoteResult",
                params: ({ event }) => toQuoteResult(event.params.result),
              },
              {
                type: "setQuoteInput",
                params: ({ event }) => event.params.quoteInput,
              },
              "syncExactInputQuoteAmountToForm",
              "clearExactInputQuoteToAmountSync",
            ],
          },
          {
            target: ".quoting",
            guard: "isQuotable",
            actions: [
              "clearQuoteResult",
              "clearQuoteInput",
              "clearQuoteRejectionReason",
            ],
          },
        ],

        WITHDRAW_1CS_QUOTE_ERROR: {
          actions: [
            {
              type: "setQuoteResult",
              params: ({ event }) => ({
                tag: "err" as const,
                value: { reason: event.params.reason },
              }),
            },
            {
              type: "clearQuoteInput",
            },
            "clearExactInputQuoteToAmountSync",
          ],
        },

        REQUEST_REVIEW: [
          {
            target: "#withdraw-ui.submitting",
            guard: and(["isQuoteOk", "isWithdrawParamsComplete"]),
            actions: [
              "setAnalyticsJourney",
              "emitAnalyticsJourneyStarted",
              "clearIntentCreationResult",
              "clearAmountModeFallbackNotice",
              "clearQuoteRejectionReason",
              { type: "setSubmitDeps", params: ({ event }) => event.params },
              "pauseQuoter",
            ],
          },
        ],

        CANCEL_REVIEW: {
          target: ".idle",
          actions: [
            "clearAmountModeFallbackNotice",
            "clearQuoteRejectionReason",
          ],
        },
      },

      states: {
        idle: {},

        quoting: {
          entry: ["clearQuoteResult", "clearQuoteInput", "requestQuote"],
          on: {
            NEW_WITHDRAW_1CS_QUOTE: [
              {
                target: "quoting",
                ...fallbackFreshExactOutOverBalanceTransition,
              },
              {
                target: "idle",
                guard: "isFreshExactOutOverBalance",
                actions: [
                  "clearAmountModeFallbackNotice",
                  {
                    type: "setQuoteRejectionReason",
                    params: { reason: "EXCEEDS_BALANCE" },
                  },
                  {
                    type: "setQuoteResult",
                    params: ({ event }) => toQuoteResult(event.params.result),
                  },
                  {
                    type: "setQuoteInput",
                    params: ({ event }) => event.params.quoteInput,
                  },
                  "clearExactInputQuoteToAmountSync",
                ],
              },
              {
                target: "idle",
                guard: "isFreshWithdrawQuote",
                actions: [
                  "clearAmountModeFallbackNotice",
                  "clearQuoteRejectionReason",
                  {
                    type: "setQuoteResult",
                    params: ({ event }) => toQuoteResult(event.params.result),
                  },
                  {
                    type: "setQuoteInput",
                    params: ({ event }) => event.params.quoteInput,
                  },
                  "syncExactInputQuoteAmountToForm",
                  "clearExactInputQuoteToAmountSync",
                ],
              },
              {
                target: "quoting",
                reenter: true,
                guard: "isQuotable",
                actions: [
                  "clearQuoteResult",
                  "clearQuoteInput",
                  "clearQuoteRejectionReason",
                ],
              },
              { target: "idle" },
            ],
            WITHDRAW_1CS_QUOTE_ERROR: {
              target: "idle",
              actions: [
                {
                  type: "setQuoteResult",
                  params: ({ event }) => ({
                    tag: "err" as const,
                    value: { reason: event.params.reason },
                  }),
                },
                { type: "clearQuoteInput" },
                "clearExactInputQuoteToAmountSync",
              ],
            },
          },
        },

        reviewing: {
          on: {
            CONFIRM_WITHDRAWAL: {
              target: "#withdraw-ui.submitting",
              guard: "isIntentCreationResultError",
              actions: [
                "setAnalyticsJourney",
                "emitAnalyticsJourneyStarted",
                "clearIntentCreationResult",
                "clearExecutionQuote",
                "clearFreshnessGateRequirement",
              ],
            },
          },
        },
      },
    },

    submitting: {
      invoke: {
        id: "withdrawRef",
        src: "withdraw1csActor",

        input: ({ context, self }) => {
          assert(context.submitDeps, "submitDeps is null")
          assert(context.quoteInput, "quoteInput is null")

          const formValues = context.withdrawFormRef.getSnapshot().context
          const quoteInput = context.quoteInput

          return {
            tokenIn: quoteInput.tokenIn,
            tokenOut: formValues.tokenOut,
            tokenOutDeployment: formValues.tokenOutDeployment,
            destinationNetwork: formValues.blockchain,
            swapType: context.amountMode,
            slippageBasisPoints: quoteInput.slippageBasisPoints,
            defuseUserId: authIdentity.authHandleToIntentsUserId(
              context.submitDeps.userAddress,
              context.submitDeps.userChainType
            ),
            deadline: new Date(
              Date.now() + settings.swapExpirySec * 1000
            ).toISOString(),
            userAddress: context.submitDeps.userAddress,
            userChainType: context.submitDeps.userChainType,
            nearClient: context.submitDeps.nearClient,
            amountIn: quoteInput.amount,
            recipient: quoteInput.recipient,
            ...(quoteInput.destinationMemo
              ? { destinationMemo: quoteInput.destinationMemo }
              : {}),
            ...(!isNearIntentsNetwork(formValues.blockchain) &&
            isAuroraVirtualChain(formValues.tokenOutDeployment.chainName) &&
            formValues.parsedRecipient?.kind === "destination"
              ? {
                  virtualChainRecipient: formValues.parsedRecipient.address,
                }
              : {}),
            destinationChainName: formValues.tokenOutDeployment.chainName,
            ...(context.isConfidential
              ? { isConfidential: context.isConfidential }
              : {}),
            minAmountOut:
              context.quoteResult?.tag === "ok"
                ? (() => {
                    const amountOut = safeParseBigInt(
                      context.quoteResult.value.quote.amountOut
                    )
                    if (amountOut == null) return undefined
                    return netDownAmount(
                      amountOut,
                      quoteInput.slippageBasisPoints
                    )
                  })()
                : undefined,
            previousOppositeAmount:
              context.quoteResult?.tag === "ok"
                ? {
                    amount: BigInt(context.quoteResult.value.quote.amountOut),
                    decimals: formValues.tokenOut.decimals,
                  }
                : { amount: 0n, decimals: formValues.tokenOut.decimals },
            parentRef: self,
          }
        },

        onDone: [
          {
            target: "#withdraw-ui.editing.reviewing",
            guard: { type: "isOk", params: ({ event }) => event.output },
            actions: [
              "clearFreshnessGateRequirement",
              "clearExecutionQuote",
              {
                type: "setIntentCreationResult",
                params: ({ event }) => event.output,
              },
              sendTo("depositedBalanceRef", ({ event }) => {
                assert(event.output.tag === "ok")
                return {
                  type: "ADD_ACCOUNT",
                  params: {
                    accountId: authIdentity.authHandleToIntentsUserId(
                      event.output.value.depositAddress,
                      "near"
                    ),
                  },
                }
              }),
              "emitEventIntentPublished",
            ],
          },
          {
            target: "#withdraw-ui.editing.reviewing",
            actions: [
              "clearFreshnessGateRequirement",
              "clearExecutionQuote",
              {
                type: "emitWithdrawalFailed",
                params: ({ event }) => event.output,
              },
              {
                type: "setIntentCreationResult",
                params: ({ event }) => event.output,
              },
            ],
          },
        ],

        onError: {
          target: "#withdraw-ui.editing.reviewing",
          actions: [
            "clearFreshnessGateRequirement",
            "clearExecutionQuote",
            {
              type: "logError",
              params: ({ event }) => event,
            },
            {
              type: "setIntentCreationResult",
              params: ({ event }): Withdraw1csMachineOutput => ({
                tag: "err",
                value: {
                  reason: "ERR_CANNOT_PUBLISH_INTENT",
                  error:
                    event.error instanceof Error
                      ? event.error
                      : new Error(String(event.error)),
                },
              }),
            },
          ],
        },
      },

      initial: "fetchingQuote",

      on: {
        EXECUTION_QUOTE_READY: [
          {
            target: ".awaitingConfirmation",
            guard: {
              type: "isExecutionQuoteAffordable",
              params: ({ event }) => event.params,
            },
            actions: [
              {
                type: "updateQuoteAmounts",
                params: ({ event }) => ({
                  newAmountIn: event.params.newAmountIn.amount,
                  newAmountOut: event.params.newOppositeAmount.amount,
                }),
              },
              {
                type: "setExecutionQuote",
                params: ({ event }) => ({
                  newOppositeAmount: event.params.newOppositeAmount,
                  previousOppositeAmount: event.params.previousOppositeAmount,
                }),
              },
            ],
          },
          {
            target: "#withdraw-ui.submitting",
            reenter: true,
            actions: [
              {
                type: "applyExactInFallback",
                params: { notice: "execution_balance_limit" as const },
              },
              "clearExecutionQuote",
            ],
          },
        ],
      },

      states: {
        fetchingQuote: {
          on: {
            FRESH_DESTINATION_GATE_CHANGED: {
              actions: {
                type: "setFreshnessGateRequirement",
                params: ({ event }) => event.params,
              },
            },
            CANCEL_REVIEW: {
              target: "#withdraw-ui.editing.idle",
              actions: [
                "clearFreshnessGateRequirement",
                "clearExecutionQuote",
                "clearAmountModeFallbackNotice",
                "clearQuoteRejectionReason",
              ],
            },
          },
        },

        awaitingConfirmation: {
          on: {
            CONFIRM_WITHDRAWAL: {
              guard: "freshnessGateSatisfied",
              target: "processing",
              actions: [
                "emitFreshnessWarningProceeded",
                "clearFreshnessGateRequirement",
                "clearExecutionQuote",
                "sendToWithdrawRefConfirm",
              ],
            },
            FRESH_DESTINATION_GATE_CHANGED: {
              actions: {
                type: "setFreshnessGateRequirement",
                params: ({ event }) => event.params,
              },
            },
            CANCEL_REVIEW: {
              target: "#withdraw-ui.editing.idle",
              actions: [
                "clearFreshnessGateRequirement",
                "clearExecutionQuote",
                "clearAmountModeFallbackNotice",
                "clearQuoteRejectionReason",
              ],
            },
          },
        },

        processing: {},
      },
    },
  },

  initial: "editing",
})
